import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal, Trash2, Flag, Loader2 } from 'lucide-react'
import type { Reply } from '@olimhub/shared'
import { Avatar } from '@/components/profile/Avatar'
import { LanguageBadge } from '@/components/common/LanguageBadge'
import { ReportModal } from '@/components/common/ReportModal'
import { renderContent } from '@/lib/content-renderer'
import { formatPostTime } from '@/lib/dates'
import { accountPath } from '@/lib/accountPath'
import { useAuthStore } from '@/store/auth.store'
import { postsApi } from '@/api'
import { useToast } from '@/components/common/Toast'

interface ReplyCardProps {
  reply: Reply
  onDeleted: (id: string) => void
}

export function ReplyCard({ reply, onDeleted }: ReplyCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { account, hasCapability } = useAuthStore()
  const { toast } = useToast()
  const [showMenu, setShowMenu] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isOwn = !!account && account.id === reply.account.id
  const isAdmin = hasCapability('moderate')
  const html = renderContent(reply.content)
  const dir: 'ltr' | 'rtl' = reply.language === 'he' ? 'rtl' : 'ltr'

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await postsApi.delete(reply.id)
      onDeleted(reply.id)
    } catch {
      toast.error('Could not delete reply.')
    } finally {
      setIsDeleting(false)
      setShowMenu(false)
    }
  }

  const iconBtnBase: React.CSSProperties = {
    border: 'none', background: 'none', cursor: 'pointer',
    color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 4, borderRadius: 'var(--radius-sm)',
    flexShrink: 0,
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        paddingInlineStart: 12,
        borderInlineStart: '2px solid var(--border)',
      }}
    >
      {/* Avatar */}
      <button
        type="button"
        onClick={() => navigate(accountPath(reply.account))}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0, display: 'flex', alignSelf: 'flex-start', marginTop: 2 }}
      >
        <Avatar
          name={reply.account.displayName || reply.account.username}
          avatarUrl={reply.account.avatarUrl}
          animalAvatar={reply.account.animalAvatar}
          accountType={reply.account.accountType}
          size="sm"
        />
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header row: name · badge · actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={() => navigate(accountPath(reply.account))}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-sm)',
              color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', flex: 1, minWidth: 0, textAlign: 'start',
            }}
          >
            {reply.account.displayName || reply.account.username}
          </button>

          <LanguageBadge lang={reply.language} size="sm" />

          {/* ── Actions ─────────────────────────────────────────────────────── */}

          {/* Report button — shown for others (and always for admin) */}
          {account && (!isOwn || isAdmin) && (
            <button
              type="button"
              aria-label="Report reply"
              onClick={() => setShowReport(true)}
              style={iconBtnBase}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--danger)' }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)' }}
            >
              <Flag size={13} />
            </button>
          )}

          {/* Three-dot delete menu — own reply OR admin */}
          {(isOwn || isAdmin) && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                aria-label="Reply options"
                onClick={() => setShowMenu((v) => !v)}
                style={iconBtnBase}
              >
                <MoreHorizontal size={14} />
              </button>

              {showMenu && (
                <div
                  style={{
                    position: 'absolute', top: '100%', insetInlineEnd: 0, zIndex: 50,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-modal)',
                    minWidth: 120, overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={handleDelete}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', border: 'none', background: 'none',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)',
                      color: 'var(--danger)', textAlign: 'start', opacity: isDeleting ? 0.6 : 1,
                    }}
                  >
                    {isDeleting
                      ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Trash2 size={13} />}
                    {t('post.delete')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* @username · time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
          <button
            type="button"
            onClick={() => navigate(accountPath(reply.account))}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)',
              color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', minWidth: 0,
            }}
          >
            @{reply.account.username}
          </button>
          <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-xs)' }}>·</span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)', flexShrink: 0 }}>
            {formatPostTime(reply.createdAt)}
          </span>
        </div>

        {/* Content */}
        <div
          dir={dir}
          dangerouslySetInnerHTML={{ __html: html }}
          onClick={(e) => {
            const target = e.target as HTMLElement
            if (target.tagName === 'A') {
              e.stopPropagation()
              if (target.classList.contains('md-tag')) {
                e.preventDefault()
                navigate(`/?tag=${target.dataset.tag ?? ''}`)
              } else if (target.classList.contains('md-mention')) {
                e.preventDefault()
                navigate(`/@${target.dataset.username ?? ''}`)
              }
            }
          }}
          style={{
            marginTop: 6,
            fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)',
            color: 'var(--text-body)', lineHeight: 'var(--lh-normal)', wordBreak: 'break-word',
          }}
        />
      </div>

      <ReportModal
        open={showReport}
        onClose={() => setShowReport(false)}
        postId={reply.id}
      />
    </div>
  )
}
